import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

export default function ForgotPassword() {
  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { forgotPassword, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setSuccess('Verification code sent to your email.');
      setStep('confirm');
    } catch (err) {
      console.error('[ForgotPassword] Request error:', err);
      const messages = {
        UserNotFoundException: 'No account found with this email.',
        LimitExceededException: 'Too many attempts. Please try again later.',
      };
      setError(messages[err.code] || err.message || 'Failed to send code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(email, code, newPassword);
      setSuccess('Password reset successfully!');
      setTimeout(() => {
        navigate('/login', { state: { message: 'Password reset. Please sign in.' } });
      }, 2000);
    } catch (err) {
      console.error('[ForgotPassword] Reset error:', err);
      const messages = {
        CodeMismatchException: 'Invalid verification code.',
        ExpiredCodeException: 'Code has expired. Please request a new one.',
        InvalidPasswordException: 'Password must be at least 8 characters.',
      };
      setError(messages[err.code] || err.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>{step === 'request' ? 'Reset Password' : 'Enter New Password'}</h1>
        <p className="auth-subtitle">
          {step === 'request'
            ? "We'll send a verification code to your email"
            : `Code sent to ${email}`}
        </p>

        {error && <div className="auth-error">{error}</div>}
        {success && step === 'confirm' && !error && (
          <div className="auth-success">{success}</div>
        )}

        {step === 'request' ? (
          <form onSubmit={handleRequestCode}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Enter 6-digit code"
                autoComplete="one-time-code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="auth-links">
          <Link to="/login">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}
